/**
 * In-memory fixture implementation of CandidateApiClient.
 *
 * Deterministic and offline: it drives the OTP sign-in flow and seeds a full ledger — all SEVEN
 * card states, including the `unlocked` ("Letter") state the Phase-1 HTTP wire cannot yet produce
 * (see ledger.ts / DESIGN-NOTES.md). The demo cast is the design-spec one (Jane Doe is the signed-in
 * candidate; her referees are Sam Whitmore, Marcus Bell, Aroha Ngata, Dana Osei, Tom Alder, Priya
 * Nair, Hemi Walker), so demos read consistently with the referee flow and the mocks.
 *
 * WP-F8b adds the request lifecycle (create / detail+timeline / resend / cancel) and the letter +
 * unlock content, all against a MUTABLE store so the wizard's new request appears in the ledger and
 * a cancel removes it. The store is module-level and resettable (`resetMockLedger`) so tests start
 * from the same seed; a client created with explicit `initialCards` gets an isolated store instead.
 */
import type { TokenResponse } from './wire';
import type { LedgerCard, LedgerCardState } from './ledger';
import { CandidateApiError } from './errors';
import { buildReferenceContent, type ReferenceContent } from './letter-content';
import type {
  CandidateApiClient,
  CheckoutSession,
  CreateRequestResult,
  NewRequestInput,
  PdfDownload,
  ReferralInfo,
  RequestDetail,
  RequestTimelineEntry,
  UnlockOptions,
  WaitlistInput,
} from './api-client';

/** Minutes/hours/days ago as an ISO string, for readable relative-time meta lines. */
function minutesAgo(mins: number): string {
  return new Date(Date.now() - mins * 60_000).toISOString();
}

/** A loose "has a local part and a dotted domain" check — mirrors the API's own deliberately-loose gate. */
function looksLikeEmail(email: string): boolean {
  const at = email.indexOf('@');
  return at > 0 && at < email.length - 1 && email.indexOf('.', at) > at;
}

function simpleHash(s: string): string {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

interface SeedRow {
  name: string;
  /** The referee's email — the server's duplicate key is its hash, so the mock dedupes on this too. */
  email: string;
  company: string | null;
  state: LedgerCardState;
  status: string;
  /** Which timestamp the state's meta line reads from. */
  at: string;
}

/**
 * The seven-state demo ledger, newest-activity-first (the order the server returns and the screen
 * keeps). Each row carries the wire `status` that the state derives from, except `unlocked`, whose
 * `status` stays `signed` because the ledger wire has no post-sign entitlement state — the `unlocked`
 * card is a seam-level fixture for the entitlement the real ledger wire still cannot carry (the
 * entitlement is read from the unlock-options endpoint, not the ledger — DESIGN-NOTES / WP-F11a).
 */
const DEMO_ROWS: SeedRow[] = [
  { name: 'Sam Whitmore', email: 'sam@example.com', company: 'Northwind Studio', state: 'in_progress', status: 'processing', at: minutesAgo(1) },
  { name: 'Marcus Bell', email: 'marcus@example.com', company: 'Northwind Studio', state: 'ready', status: 'signed', at: minutesAgo(2) },
  { name: 'Aroha Ngata', email: 'aroha@example.com', company: 'Kōwhai Legal', state: 'opened', status: 'viewed', at: minutesAgo(12) },
  { name: 'Dana Osei', email: 'dana@example.com', company: 'Harbourline Joinery', state: 'sent', status: 'sent', at: minutesAgo(64) },
  { name: 'Tom Alder', email: 'tom@example.com', company: 'Northwind Studio', state: 'unlocked', status: 'signed', at: minutesAgo(1440) },
  { name: 'Priya Nair', email: 'priya@example.com', company: null, state: 'declined', status: 'declined', at: minutesAgo(2880) },
  { name: 'Hemi Walker', email: 'hemi@example.com', company: 'Southerly Freight', state: 'expired', status: 'expired', at: minutesAgo(20160) },
];

function seedCard(row: SeedRow, i: number): LedgerCard {
  const nullTs = null;
  return {
    id: `req_${simpleHash(row.name)}_${i}`,
    refereeName: row.name,
    company: row.company,
    status: row.status,
    state: row.state,
    requestedAt: row.at,
    sentAt: row.state === 'sent' ? row.at : minutesAgo(20160),
    respondedAt: ['opened', 'in_progress', 'ready', 'unlocked', 'declined'].includes(row.state) ? row.at : nullTs,
    signedAt: row.state === 'ready' || row.state === 'unlocked' ? row.at : nullTs,
    expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60_000).toISOString(),
    closedAt: ['unlocked', 'declined', 'expired'].includes(row.state) ? row.at : nullTs,
  };
}

/** The default demo note surfaced on request detail for seeded rows (04-copy voice). */
const DEMO_NOTE = 'Thanks so much for doing this — it means a lot. No rush at all.';

/** The demo candidate's own referral code (design-spec cast — Jane Doe). Claiming it is a self-referral. */
const MOCK_REFERRAL_CODE = 'JANE7Q2K';

/** The mutable mock world: the ledger store, per-request notes, and who "self" is (self-referee). */
interface MockState {
  cards: LedgerCard[];
  notes: Map<string, string | null>;
  /**
   * Referee email per card id (lowercased). Kept OFF the `LedgerCard` view-model — the referee's
   * address is not the candidate's to display — but held here because the server's duplicate key is
   * the email hash, so the mock dedupes on email to match it (see `createRequest`).
   */
  emails: Map<string, string>;
  /** The signed-in candidate's own email — entering it as a referee is a self-referee (400). */
  selfEmail: string;
  /** Whether a referral code has already been attached to this account (attribution is immutable). */
  referralClaimed: boolean;
  /**
   * The in-memory entitlement store (WP-F11a stand-in for the server's `reference_unlocks`): ids of
   * references this candidate has unlocked. A one-tap / paid unlock adds to it; `getUnlockOptions`
   * and `getReference` read it. It is the source of truth for "unlocked", NOT the card state — a
   * `ready` reference unlocks without the ledger wire ever gaining an entitlement field.
   */
  unlockedIds: Set<string>;
  /** The candidate's base free-unlock credit balance (04-copy: "You have 1 free unlock"). */
  freeCredits: number;
  /** Earned referral-bonus unlocks (0 by default — a bonus fires when someone claims the code). */
  referralBonuses: number;
}

function freshState(overrides: Partial<Pick<MockState, 'freeCredits' | 'referralBonuses'>> = {}): MockState {
  const cards = DEMO_ROWS.map(seedCard);
  const notes = new Map<string, string | null>(cards.map((c) => [c.id, DEMO_NOTE]));
  const emails = new Map<string, string>(cards.map((c, i) => [c.id, DEMO_ROWS[i].email.toLowerCase()]));
  // Jane Doe is the demo candidate (design-spec cast); her address is the default "self".
  return {
    cards,
    notes,
    emails,
    selfEmail: 'jane@example.com',
    referralClaimed: false,
    unlockedIds: new Set<string>(),
    freeCredits: overrides.freeCredits ?? 1,
    referralBonuses: overrides.referralBonuses ?? 0,
  };
}

/**
 * Loosely mirror the backend's `ReferralCode.TryNormalize`: trim the ends, then strip ONLY separator
 * hyphens and spaces from the interior (people group codes as `ABCD-EFGH`), uppercase, and require
 * exactly 8 characters of the claimable alphabet. Returns null for anything that could never have
 * been minted — the caller folds malformed and unknown codes into one answer.
 *
 * Note the interior strip is hyphen/space ONLY, not `\s`: a tab or newline in the middle is NOT a
 * separator, so it fails the alphabet/length check exactly as the server's char-by-char loop does.
 */
const REFERRAL_ALPHABET = 'ABCDEFGHJKMNPQRSTVWXYZ23456789';
function normalizeReferralCode(raw: string): string | null {
  const stripped = raw.trim().replace(/[- ]/g, '').toUpperCase();
  if (stripped.length !== 8) return null;
  for (const ch of stripped) {
    if (!REFERRAL_ALPHABET.includes(ch)) return null;
  }
  return stripped;
}

/** Module-level store shared by the singleton mock client; reset between tests. */
let moduleState = freshState();

/** Re-seed the shared mock store to its initial seven-state ledger (call in test `beforeEach`). */
export function resetMockLedger(): void {
  moduleState = freshState();
}

/** The EU-referee compliance flag the backend sets — approximated here by a handful of EU TLDs. */
function looksEu(email: string): boolean {
  const domain = email.slice(email.lastIndexOf('@') + 1).toLowerCase();
  return /\.(eu|de|fr|ie|nl|es|it|be|at|pt|fi|se|dk|pl)$/.test(domain);
}

/**
 * Synthesize a plausible ordered status timeline for a card's current state. The wire returns real
 * transitions; the mock reconstructs a believable path from the card's state + timestamps so the
 * detail screen has something faithful to render offline.
 */
function synthesizeTimeline(card: LedgerCard): RequestTimelineEntry[] {
  const path: { status: string; actor: string }[] = [{ status: 'sent', actor: 'system' }];
  switch (card.state) {
    case 'opened':
      path.push({ status: 'viewed', actor: 'referee' });
      break;
    case 'in_progress':
      path.push({ status: 'viewed', actor: 'referee' }, { status: 'accepted', actor: 'referee' });
      if (card.status !== 'accepted') path.push({ status: 'recorded', actor: 'referee' });
      if (['processing', 'drafted', 'needs_review'].includes(card.status)) {
        path.push({ status: 'processing', actor: 'system' });
      }
      if (['drafted', 'needs_review'].includes(card.status)) {
        path.push({ status: card.status, actor: card.status === 'needs_review' ? 'system' : 'referee' });
      }
      break;
    case 'ready':
    case 'unlocked':
      path.push(
        { status: 'viewed', actor: 'referee' },
        { status: 'accepted', actor: 'referee' },
        { status: 'recorded', actor: 'referee' },
        { status: 'processing', actor: 'system' },
        { status: 'drafted', actor: 'referee' },
        { status: 'signed', actor: 'referee' },
      );
      break;
    case 'declined':
      path.push({ status: 'viewed', actor: 'referee' }, { status: 'declined', actor: 'referee' });
      break;
    case 'expired':
      path.push({ status: 'expired', actor: 'system' });
      break;
    case 'sent':
    default:
      break;
  }

  // Spread timestamps between the request's creation and its latest known activity.
  const start = new Date(card.requestedAt).getTime();
  const endIso = card.closedAt ?? card.signedAt ?? card.respondedAt ?? card.sentAt ?? card.requestedAt;
  const end = Math.max(new Date(endIso).getTime(), start);
  const span = end - start;
  return path.map((step, i) => ({
    fromStatus: i === 0 ? null : path[i - 1].status,
    toStatus: step.status,
    actor: step.actor,
    occurredAt: new Date(start + (path.length > 1 ? (span * i) / (path.length - 1) : 0)).toISOString(),
  }));
}

/** A tiny but valid single-page PDF, so the letter-viewer download produces a real file offline. */
function stubPdfBlob(refereeName: string): Blob {
  const text = `By My Word — signed reference (demo) from ${refereeName}`;
  const body = [
    '%PDF-1.4',
    '1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj',
    '2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj',
    '3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 595 842]/Contents 4 0 R/Resources<</Font<</F1 5 0 R>>>>>>endobj',
    `4 0 obj<</Length ${text.length + 40}>>stream`,
    `BT /F1 14 Tf 60 760 Td (${text.replace(/[()\\]/g, ' ')}) Tj ET`,
    'endstream endobj',
    '5 0 obj<</Type/Font/Subtype/Type1/BaseFont/Helvetica>>endobj',
    'trailer<</Root 1 0 R>>',
    '%%EOF',
  ].join('\n');
  return new Blob([body], { type: 'application/pdf' });
}

export interface MockClientOptions {
  /** Artificial latency per call (ms). 0 in tests; a small value in dev feels real. */
  latencyMs?: number;
  /** Seed the ledger with a specific set of cards (e.g. `[]` to demo/test the empty state). */
  initialCards?: LedgerCard[];
  /** Seed the base free-unlock credit balance (default 1). */
  freeCredits?: number;
  /** Seed the earned referral-bonus balance (default 0). */
  referralBonuses?: number;
}

export function createMockClient(opts: MockClientOptions = {}): CandidateApiClient {
  const { latencyMs = 0 } = opts;
  // Any explicit seed (cards or unlock balances) gets an ISOLATED state so a caller/test cannot
  // disturb (or be disturbed by) the shared module store; otherwise share the resettable module
  // store. `initialCards` still replaces the seeded ledger (and drops its notes/emails); the balance
  // overrides start from the full default seed so the standard ready/unlocked cards are present.
  const wantsIsolated =
    opts.initialCards !== undefined || opts.freeCredits !== undefined || opts.referralBonuses !== undefined;
  let local: MockState | null = null;
  if (wantsIsolated) {
    local = freshState({ freeCredits: opts.freeCredits, referralBonuses: opts.referralBonuses });
    if (opts.initialCards) {
      local.cards = opts.initialCards;
      local.notes = new Map();
      local.emails = new Map();
    }
  }
  const st = (): MockState => local ?? moduleState;
  const wait = () => (latencyMs > 0 ? new Promise<void>((r) => setTimeout(r, latencyMs)) : Promise.resolve());
  const find = (id: string): LedgerCard | undefined => st().cards.find((c) => c.id === id);
  /** A reference is unlocked if it is in the entitlement store or seeded already-unlocked. */
  const isUnlocked = (id: string): boolean => st().unlockedIds.has(id) || find(id)?.state === 'unlocked';
  /**
   * Grant an unlock: record the entitlement AND flip the ledger card to the `unlocked` ("Letter")
   * state so the mock ledger reflects it (the demo shows the card change; the real HTTP ledger wire
   * cannot — DESIGN-NOTES). Idempotent.
   */
  const grantUnlock = (id: string): void => {
    st().unlockedIds.add(id);
    const card = find(id);
    if (card && card.state !== 'unlocked') {
      card.state = 'unlocked';
      card.status = 'signed';
      card.closedAt = new Date().toISOString();
    }
  };

  return {
    async requestOtp(email) {
      await wait();
      if (!looksLikeEmail(email.trim())) {
        throw new CandidateApiError('Invalid email', 'server', 400, 'invalid_email');
      }
      // Neutral: nothing is returned and nothing branches on existence, exactly like the real route.
    },

    async verifyOtp(email, code) {
      await wait();
      const trimmed = code.trim();
      // Any 4+ digit numeric code signs in (offline demo); a non-numeric code is the wrong-code path.
      if (!/^\d{4,}$/.test(trimmed)) {
        throw new CandidateApiError('That code did not work', 'unauthorized', 401, 'invalid_code');
      }
      // Remember who signed in so a self-referee is detected against the real address.
      st().selfEmail = email.trim().toLowerCase();
      const now = Date.now();
      return {
        accessToken: `mock-access.${simpleHash(email)}`,
        accessTokenExpiresAt: new Date(now + 15 * 60_000).toISOString(),
        refreshToken: `mock-refresh.${simpleHash(email + ':r')}`,
        refreshTokenExpiresAt: new Date(now + 14 * 24 * 60 * 60_000).toISOString(),
        userId: `mock-user-${simpleHash(email)}`,
      } satisfies TokenResponse;
    },

    async getLedger() {
      await wait();
      // Return copies so a consumer can never mutate the fixture store.
      return st().cards.map((c) => ({ ...c }));
    },

    async createRequest(input: NewRequestInput): Promise<CreateRequestResult> {
      await wait();
      const email = input.refereeEmail.trim();
      const name = input.refereeName.trim();
      if (!name || !looksLikeEmail(email)) {
        throw new CandidateApiError('Add a name and a valid email', 'server', 400, 'invalid_request');
      }
      // The referee-permission attestation, in the wire's order: the endpoint's shape check
      // (invalid_request) runs first, then the service refuses an unconfirmed create before any
      // other rule and before any write. Its own 400 code, not invalid_request — "we could not read
      // the email" and "you did not say you had asked them" are different repairs, and the wizard
      // points at a different control for each.
      if (!input.refereePermissionConfirmed) {
        throw new CandidateApiError(
          'Confirm you have asked this person',
          'server',
          400,
          'referee_permission_required',
        );
      }
      if (email.toLowerCase() === st().selfEmail) {
        throw new CandidateApiError('That is your own address', 'server', 400, 'self_referee');
      }
      // Sentinel for the suppression path so the surface is demoable/testable offline.
      if (/^suppressed@/i.test(email) || email.toLowerCase().includes('+suppressed')) {
        throw new CandidateApiError('That referee cannot be contacted', 'server', 422, 'referee_suppressed');
      }
      // Duplicate = the same referee EMAIL already has a LIVE request (not a terminal one). The
      // server dedupes on the email hash (ActiveDuplicateExistsAsync), never the display name, so
      // the mock matches: two different people at the same address collide; the same name at a new
      // address does not. (NewRequest.tsx keeps a softer same-NAME hint client-side — a courtesy,
      // not the rule; the server/mock email check is authoritative.)
      const emailLc = email.toLowerCase();
      const live = st().cards.filter((c) => !['declined', 'expired', 'unlocked'].includes(c.state));
      if (live.some((c) => st().emails.get(c.id) === emailLc)) {
        throw new CandidateApiError('You have already asked this person', 'conflict', 409, 'duplicate_request');
      }

      const now = new Date().toISOString();
      const id = `req_${simpleHash(name + email + Date.now())}`;
      const card: LedgerCard = {
        id,
        refereeName: name,
        company: input.companyName?.trim() || null,
        status: 'sent',
        state: 'sent',
        requestedAt: now,
        sentAt: now,
        respondedAt: null,
        signedAt: null,
        expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60_000).toISOString(),
        closedAt: null,
      };
      st().cards.unshift(card);
      st().notes.set(id, input.personalNote?.trim() || null);
      st().emails.set(id, emailLc);
      return { requestId: id, status: 'sent', euRefereeFlagged: looksEu(email) };
    },

    async getRequestDetail(id: string): Promise<RequestDetail> {
      await wait();
      const card = find(id);
      if (!card) {
        throw new CandidateApiError('No such request', 'not_found', 404, 'not_found');
      }
      return {
        card: { ...card },
        personalNote: st().notes.has(id) ? (st().notes.get(id) ?? null) : DEMO_NOTE,
        timeline: synthesizeTimeline(card),
      };
    },

    async resendRequest(id: string): Promise<void> {
      await wait();
      const card = find(id);
      if (!card) throw new CandidateApiError('No such request', 'not_found', 404, 'not_found');
      // Server ResendableStates = Requested/Sent/Viewed → candidate-facing sent/opened. Expired is
      // terminal server-side (no live respond edge to re-issue against), so it answers 409
      // not_resendable — never a resend that revives the card. The recovery for an expired invite
      // is a fresh request, surfaced as the detail screen's "Ask someone else" branch.
      if (!['sent', 'opened'].includes(card.state)) {
        throw new CandidateApiError('Not resendable', 'conflict', 409, 'not_resendable');
      }
      const sent = new Date(card.sentAt ?? card.requestedAt).getTime();
      if (Date.now() - sent < 48 * 60 * 60 * 1000) {
        throw new CandidateApiError('Too soon to resend', 'rate_limited', 429, 'resend_too_soon');
      }
      // A successful resend re-enqueues the invite but does NOT move the ledger's sentAt — the server
      // advances a separate last_invite_enqueued_at and leaves sentAt at the original send. Mutating
      // sentAt here would misrepresent the wire and re-open the client cooldown gate at the wrong
      // moment; the detail screen tracks the resend time client-side instead (timeline.laterIso).
    },

    async cancelRequest(id: string): Promise<void> {
      await wait();
      const card = find(id);
      if (!card) throw new CandidateApiError('No such request', 'not_found', 404, 'not_found');
      // Candidate cancel edge is legal only from draft/requested/sent → candidate-facing `sent`.
      // Once viewed (`opened`) the only cancel edge is the SystemOnly erasure path, so a candidate
      // withdrawal is refused with 409 cannot_cancel — matching CancelAsync.
      if (card.state !== 'sent') {
        throw new CandidateApiError('Cannot cancel', 'conflict', 409, 'cannot_cancel');
      }
      // Withdrawn requests are filtered from the ledger (ledger.ts: `cancelled` → hidden).
      st().cards = st().cards.filter((c) => c.id !== id);
    },

    async getUnlockOptions(id: string): Promise<UnlockOptions> {
      await wait();
      const card = find(id);
      if (!card) throw new CandidateApiError('No such request', 'not_found', 404, 'not_found');
      const alreadyUnlocked = isUnlocked(id);
      return {
        alreadyUnlocked,
        freeCreditAvailable: !alreadyUnlocked && st().freeCredits > 0,
        referralBonusAvailable: !alreadyUnlocked && st().referralBonuses > 0,
      };
    },

    async unlockWithFreeCredit(id: string): Promise<void> {
      await wait();
      const card = find(id);
      if (!card) throw new CandidateApiError('No such request', 'not_found', 404, 'not_found');
      // A rail already unlocked it → 409, nothing consumed (the caller treats this as success).
      if (isUnlocked(id)) throw new CandidateApiError('Already unlocked', 'conflict', 409, 'already_unlocked');
      // 422 mirrors the server when the credit has run out (kind 'server' — 422 has no dedicated kind).
      if (st().freeCredits <= 0) throw new CandidateApiError('No free unlock', 'server', 422, 'no_free_credit');
      st().freeCredits -= 1;
      grantUnlock(id);
    },

    async unlockWithReferralBonus(id: string): Promise<void> {
      await wait();
      const card = find(id);
      if (!card) throw new CandidateApiError('No such request', 'not_found', 404, 'not_found');
      if (isUnlocked(id)) throw new CandidateApiError('Already unlocked', 'conflict', 409, 'already_unlocked');
      if (st().referralBonuses <= 0) {
        throw new CandidateApiError('No referral bonus', 'server', 422, 'no_referral_bonus');
      }
      st().referralBonuses -= 1;
      grantUnlock(id);
    },

    async startUnlockCheckout(id: string): Promise<CheckoutSession> {
      await wait();
      const card = find(id);
      if (!card) throw new CandidateApiError('No such request', 'not_found', 404, 'not_found');
      // Server pre-check: never mint a session for an already-unlocked reference (409, no charge).
      if (isUnlocked(id)) throw new CandidateApiError('Already unlocked', 'conflict', 409, 'already_unlocked');
      // OFFLINE DEMO: there is no real Stripe to visit, so the mock simulates the whole round-trip —
      // it grants the unlock now (as the webhook would) and returns a redirect to our own return
      // route pre-marked success, so the ceremony + letter reveal play when the demo lands back.
      grantUnlock(id);
      const sessionId = `cs_mock_${simpleHash(id + ':checkout')}`;
      return { sessionId, redirectUrl: `/unlock/return?status=success&session_id=${sessionId}` };
    },

    async getReference(card: LedgerCard): Promise<ReferenceContent> {
      await wait();
      const unlocked = isUnlocked(card.id);
      return buildReferenceContent(card, unlocked, {
        freeCreditAvailable: st().freeCredits > 0,
        referralBonusAvailable: st().referralBonuses > 0,
      });
    },

    async getLetterPdf(id: string): Promise<PdfDownload> {
      await wait();
      const card = find(id);
      if (!card) throw new CandidateApiError('No such request', 'not_found', 404, 'not_found');
      if (!isUnlocked(id)) {
        // Models the content chokepoint's bare 403 for a locked read.
        throw new CandidateApiError('Letter is locked', 'forbidden', 403, 'forbidden');
      }
      // Offline demo: hand back a blob object URL for a tiny valid PDF, so opening it shows a real
      // file. (The real backend returns a short-TTL presigned R2 URL — same shape, opened the same way.)
      return { status: 'ready', downloadUrl: URL.createObjectURL(stubPdfBlob(card.refereeName)) };
    },

    async joinWaitlist(input: WaitlistInput) {
      await wait();
      if (!looksLikeEmail(input.email.trim())) {
        throw new CandidateApiError('Invalid email', 'server', 400, 'invalid_email');
      }
      // Nothing to persist offline; the real endpoint records the signal (WP-B18).
    },

    async getReferral(): Promise<ReferralInfo> {
      await wait();
      // Mirrors the real `{base}/{code}` share link the server builds. `bonusEarned` stays false
      // offline — the bonus fires when someone ELSE claims this code and sends a request, which the
      // fixture can't simulate; tests exercise the earned state by stubbing this method.
      return {
        code: MOCK_REFERRAL_CODE,
        shareUrl: `https://bymyword.com/join/${MOCK_REFERRAL_CODE}`,
        bonusEarned: false,
      };
    },

    async claimReferral(code: string): Promise<void> {
      await wait();
      // Fold malformed + unknown codes into one answer (the backend's oracle-proof stance). Offline
      // there is no other account to look up, so any well-formed code that isn't the caller's own is
      // treated as a real referrer's code.
      const normalized = normalizeReferralCode(code);
      if (normalized === null) {
        throw new CandidateApiError('That code doesn’t look right', 'server', 400, 'invalid_code');
      }
      if (normalized === MOCK_REFERRAL_CODE) {
        throw new CandidateApiError('That’s your own code', 'server', 400, 'self_referral');
      }
      // Policy order mirrors the backend: already-referred is answered before eligibility.
      if (st().referralClaimed) {
        throw new CandidateApiError('A code is already attached', 'conflict', 409, 'already_referred');
      }
      // Attribution must precede first use — a candidate who has already sent a request is too late.
      if (st().cards.length > 0) {
        throw new CandidateApiError('Too late to add a code', 'conflict', 409, 'not_eligible');
      }
      st().referralClaimed = true;
    },

    async requestAccountDeletion() {
      await wait();
      // The real endpoint anonymises server-side (WP-B18); the fixture just acknowledges.
    },
  };
}
